/**
 * RD-EST-RESP-001 — the ONE allowlist of routes that render BEFORE `BootGate`.
 *
 * ── WHY AN EXEMPTION EXISTS AT ALL ──────────────────────────────────────────
 * `BootGate` (App.js) holds EVERY route behind
 * `authLoading || !localeReady || !messagesReady`. That is exactly right for the
 * app: it prevents the first-visit language flash and the post-sign-in locale
 * ping-pong. It is exactly wrong for `/estimate`, which is a PUBLIC customer
 * page with no session to bootstrap and no react-intl to feed — so the barrier
 * would make a paying customer wait on a Supabase auth round-trip AND the
 * ~330KB EN catalog chunk before a page that needs neither can paint.
 *
 * ── WHY THIS IS A NAMED, EXACT-MATCH PREDICATE ──────────────────────────────
 * `BootGate` guards the shell that all five roles share (admin, billing,
 * worker, supervisor, platform). An exemption that matched one authenticated
 * route by accident would render that route with `authLoading` still true — a
 * shell regression, and on a security-relevant boundary. So:
 *
 *   • EXACT membership in a frozen list. Never `startsWith`, never `includes`,
 *     never a regex with an open tail. `/estimate-admin`, `/app/estimate`,
 *     `/estimate/anything` are all NOT exempt.
 *   • Every ambiguity resolves to NOT exempt. A false negative costs a public
 *     visitor a splash; a false positive is the regression above.
 *
 * Adding an entry here is a shared-shell change: it needs the same review as
 * touching `BootGate` itself.
 *
 * ── THE ONE NARROWER WAIVER (RD-SEC-036) ────────────────────────────────────
 * `LINK_SESSION_PRE_AUTH_PATHS` waives ONLY the saved-language wait
 * (`localeReady`) — never `authLoading`, never `messagesReady` — on the two
 * pages whose one-time link signs a session in on the page. Same exact-match
 * rule. Both render inside `PreAuthEnglish`, so they (and Login during the
 * sign-in hold) wait for the EN catalog (`englishReady`) instead of the saved
 * language's: `messagesReady` waits for the ACTIVE catalog (KAN-857), and a
 * pending Spanish chunk must not blank an English-only page mid-flow.
 */

/**
 * Public, session-less routes. Keep this list as short as it can possibly be.
 * KAN-1188: these are also the CUSTOMER pages (a company's customers open them
 * from an email; they never sign in) — so the same list keeps the CrewVex splash
 * off them (BootRouteSplashTrigger, SplashOverlay, public/splash-phase.js, which
 * mirrors this list — guarded by customerPagesNoSplash.guard.test.js).
 * `/unsubscribe` needs no session and no react-intl (English constants only).
 */
export const BOOT_GATE_EXEMPT_PATHS = Object.freeze(['/estimate', '/unsubscribe']);

const EXEMPT = new Set(BOOT_GATE_EXEMPT_PATHS);

/** Exact-match: pre-auth pages whose one-time link signs a session in on the page. */
const LINK_SESSION_PRE_AUTH_PATHS = new Set(['/accept-invite', '/reset-password']);

/**
 * @param {string} pathname - normally `useLocation().pathname`.
 * @returns {boolean} true ONLY for an exact allowlisted public route.
 */
export function isBootGateExemptPath(pathname) {
  if (typeof pathname !== 'string' || !pathname) return false;

  // `useLocation().pathname` never carries a query or fragment, but the
  // predicate is cut at the first `?`/`#` anyway so it is correct for a caller
  // that hands it a raw href-ish string: `/estimate?token=…` must stay exempt,
  // and `/app/dashboard?next=/estimate` must NOT become exempt.
  const cut = pathname.search(/[?#]/);
  const path = cut === -1 ? pathname : pathname.slice(0, cut);

  // Exactly ONE optional trailing slash is normalized away — `/estimate/` is
  // the same route to React Router. Anything else (`/estimate//`) is left
  // unmatched on purpose: unusual, and failing closed there costs nothing.
  const normalized = path.length > 1 && path.endsWith('/') ? path.slice(0, -1) : path;

  return EXEMPT.has(normalized);
}

/**
 * `BootGate`'s whole decision, as a pure function — so the one behaviour that
 * matters ("/estimate paints while auth is still bootstrapping, every other
 * route waits") is provable without mounting the app, its providers and the
 * Supabase auth stack.
 *
 * @param {object}  input
 * @param {string}  input.pathname
 * @param {boolean} input.authLoading    Supabase auth bootstrap in flight.
 * @param {boolean} input.localeReady    the displayed locale is the final one.
 * @param {boolean} input.messagesReady  the active i18n catalog chunk landed.
 * @param {boolean} [input.englishReady] the EN catalog landed (defaults to `messagesReady`).
 * @param {boolean} [input.handoffHold]  Login is mid sign-in handoff (authHandoffHold.js).
 * @returns {boolean} true ⇒ hold the splash and render nothing.
 */
export function shouldBootGateBlock({ pathname, authLoading, localeReady, messagesReady, englishReady = messagesReady, handoffHold = false }) {
  if (isBootGateExemptPath(pathname)) return false;
  if (authLoading) return true;
  // English-only screens wait for auth + the EN catalog, never the saved language:
  //   • Sign-in handoff: on the Login route, while Login holds the screen, the
  //     signed-in user's saved-language fetch must not blank it — Login keeps
  //     its on-screen language (LocaleContext) and plays its beat + morph.
  //   • One-time-link pages (RD-SEC-036): the link signs a session in ON the page,
  //     and the page renders in PreAuthEnglish, so waiting for the user's saved
  //     language buys nothing. Blanking here unmounted the page mid-flow (skeleton
  //     → blank → form) and remounted it after it had stripped the link from the
  //     URL — losing the invite's tenant_id/context.
  const englishOnly = (handoffHold && pathname === '/') || LINK_SESSION_PRE_AUTH_PATHS.has(pathname);
  if (englishOnly) return !englishReady;
  if (!messagesReady) return true;
  return !localeReady;
}

export default isBootGateExemptPath;
